import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import {
  ArrowLeft, Download, FileText, Loader2, Plus, Send, Trash2, Upload,
  Check, RotateCcw, Sparkles, Pencil, X as IconX,
  CheckCircle2, XCircle, AlertCircle,
} from 'lucide-react';
import { RequiredDocsChecklist } from '../../components/RequiredDocsChecklist';
import { RequiredDocsUploader } from '../../components/RequiredDocsUploader';
import { DocumentPreviewModal } from '../../components/DocumentPreviewModal';
import { usePortalI18n } from '../../context/PortalI18nContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TONE = {
  draft: 'bg-slate-100 text-slate-700',
  submitted: 'bg-sky-100 text-sky-800',
  in_process: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  payment_initiated: 'bg-blue-100 text-blue-800',
  paid: 'bg-emerald-600 text-white',
};

const DEFAULT_DOC_TYPES = [
  { value: 'claim_letter', label: 'Claim Letter' },
  { value: 'invoice', label: 'Invoice / Bill' },
  { value: 'bank_proof', label: 'Bank Proof' },
  { value: 'other', label: 'Other' },
];

const prettifyDocType = (slug) =>
  String(slug || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Build the doc-type dropdown by merging policy.required_documents (set by
// the admin) with the four fallback defaults. Any custom slug like "anyany"
// becomes "Anyany" in the dropdown so the secretary can actually pick it.
// When the admin has set `required_documents` on the policy, those become
// the ONLY options the secretary can pick from (with their description
// shown as a helper). The four hard-coded defaults below are used only as
// a fallback for legacy policies that have no required-docs list.
const buildDocTypeOptions = (requiredDocs, descriptions = {}) => {
  const filtered = (requiredDocs || []).map((s) => String(s || '').trim()).filter(Boolean);
  if (filtered.length > 0) {
    const seen = new Set();
    const out = [];
    for (const slug of filtered) {
      if (seen.has(slug)) continue;
      seen.add(slug);
      out.push({
        value: slug,
        label: prettifyDocType(slug),
        description: descriptions[slug] || '',
      });
    }
    return out;
  }
  return DEFAULT_DOC_TYPES.map((d) => ({ ...d, description: '' }));
};

const DEFAULT_UC_DOC_TYPES = [
  { value: 'utilization_certificate', label: 'Utilisation Certificate' },
  { value: 'completion_report',       label: 'Completion Report' },
  { value: 'completion_photo',        label: 'Photograph of Work' },
  { value: 'other',                   label: 'Other' },
];

// ─────────────────────────────────────────────────────────────────────────
// Visual claim status stepper.
// Stages are ordered linearly. Each stage is one of:
//   • completed (filled green) — the claim has progressed past it
//   • current   (ring + pulse) — current status
//   • pending   (muted)         — not yet reached
// "Sent back" is a sub-state of `in_process`: if the latest status_history
// entry tagged `sent_back=True`, we surface a yellow banner above the
// stepper so the secretary clearly sees the rollback + admin's note.
// ─────────────────────────────────────────────────────────────────────────
const STEP_KEYS = ['draft', 'submitted', 'in_process', 'approved', 'payment_initiated', 'paid'];

const ClaimTimeline = ({ claim }) => {
  const { t, tStatus } = usePortalI18n();
  // 'paid' is the final stage — every step should read as completed.
  // For every other status we use its index in STEPS as the "current" pointer.
  const isPaid = claim.status === 'paid';
  const rawIdx = Math.max(0, STEP_KEYS.findIndex((k) => k === claim.status));
  const currentIdx = isPaid ? STEP_KEYS.length : rawIdx;
  const sentBack = (claim.status_history || []).slice().reverse().find(
    (h) => h.sent_back || (h.note || '').toLowerCase().startsWith('sent back')
  );

  return (
    <div className="rounded-xl border bg-white p-4 sm:p-5 shadow-sm">
      {sentBack && claim.status !== 'approved' && claim.status !== 'paid' && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm flex items-start gap-2">
          <RotateCcw className="h-4 w-4 mt-0.5 text-amber-700 flex-shrink-0" />
          <div>
            <div className="font-semibold text-amber-900">{t('claims.detail.sentBackBanner')}</div>
            <div className="text-amber-900/80 text-xs mt-0.5">{sentBack.note}</div>
          </div>
        </div>
      )}
      <ol className="flex items-start justify-between gap-1 sm:gap-2 overflow-x-auto pt-1">
        {STEP_KEYS.map((stepKey, i) => {
          const state = i < currentIdx ? 'completed' : i === currentIdx ? 'current' : 'pending';
          const dot =
            state === 'completed'
              ? 'bg-emerald-600 border-emerald-600 text-white'
              : state === 'current'
              ? 'border-emerald-600 text-emerald-700 bg-white ring-4 ring-emerald-100'
              : 'border-slate-300 text-slate-400 bg-white';
          const label =
            state === 'completed'
              ? 'text-emerald-700'
              : state === 'current'
              ? 'text-emerald-800 font-semibold'
              : 'text-slate-400';
          const line = i <= currentIdx ? 'bg-emerald-500' : 'bg-slate-200';
          return (
            <li
              key={stepKey}
              className="flex-1 flex flex-col items-center min-w-[70px] relative"
              data-testid={`timeline-step-${stepKey}`}
            >
              {i > 0 && (
                <span
                  className={`absolute top-3.5 h-0.5 ${line}`}
                  style={{ left: 'calc(-50% + 18px)', right: 'calc(50% + 18px)' }}
                  aria-hidden
                />
              )}
              <span
                className={`relative z-10 h-7 w-7 rounded-full border-2 grid place-items-center text-xs font-bold ${dot}`}
              >
                {state === 'completed' ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className={`mt-1.5 text-[10px] sm:text-xs text-center leading-tight ${label}`}>
                {tStatus(stepKey)}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
};


const PortalClaims = () => {
  const { t, tStatus, tDocType, lang } = usePortalI18n();
  const [params, setParams] = useSearchParams();
  const policyIdFromUrl = params.get('policy');
  const claimIdFromUrl = params.get('id');

  const [claims, setClaims] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ policy_id: '', claim_amount: '', notes: '' });
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  // Detail view
  const [activeClaim, setActiveClaim] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Inline edit for claim_amount (visible only on draft / sent-back claims).
  // Local string buffer so the user can clear the field while typing.
  const [editingAmount, setEditingAmount] = useState(false);
  const [amountDraft, setAmountDraft] = useState('');
  const [savingAmount, setSavingAmount] = useState(false);

  // Delete-confirm dialog state. We track BOTH the claim being deleted and
  // a `deleting` flag separately so the AlertDialog can show a spinner on
  // the destructive button while the request is in flight.
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingClaim, setDeletingClaim] = useState(false);

  // ── Pre-submit AI gate ──────────────────────────────────────────────
  // Clicking Submit now always runs a full AI re-review first, shows the
  // verdict in a confirm modal, and only fires the real submit endpoint
  // when the secretary clicks "Confirm submission". Verdict is purely
  // informational — the button is always enabled. If the secretary edits
  // any doc after seeing the modal, the auto-rerun-on-upload kicks in and
  // they have to click Submit again to re-enter this gate.
  const [preSubmitOpen, setPreSubmitOpen] = useState(false);
  const [preSubmitRunning, setPreSubmitRunning] = useState(false);
  const [submittingConfirmed, setSubmittingConfirmed] = useState(false);

  // Attachment upload
  const [uploadingDocType, setUploadingDocType] = useState('claim_letter');
  const [uploading, setUploading] = useState(false);

  // The doc-type dropdown merges the admin-defined `required_documents` for
  // the active claim's policy with the four defaults. That way custom slugs
  // like "site_photo" or "anyany" actually show up for the secretary.
  const docTypeOptions = useMemo(
    () => buildDocTypeOptions(
      activeClaim?.policy?.required_documents,
      activeClaim?.policy?.required_documents_descriptions || {},
    ),
    [activeClaim?.policy?.required_documents, activeClaim?.policy?.required_documents_descriptions],
  );

  const activeDocTypeMeta = docTypeOptions.find((o) => o.value === uploadingDocType);

  // Keep `uploadingDocType` valid when the option set changes (e.g. after
  // navigating between two claims that have different required-doc lists).
  useEffect(() => {
    if (docTypeOptions.length === 0) return;
    if (!docTypeOptions.some((o) => o.value === uploadingDocType)) {
      setUploadingDocType(docTypeOptions[0].value);
    }
  }, [docTypeOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Utilisation-certificate upload state (post-payout) ────────────────
  const [ucDocType, setUcDocType] = useState('utilization_certificate');
  const [ucUploading, setUcUploading] = useState(false);
  const [aiRunning, setAiRunning] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const previewUrl = previewAttachment && activeClaim
    ? `${API}/portal/claims/${activeClaim.id}/attachments/${previewAttachment.id}/download`
    : null;

  const ucDocTypeOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const slug of activeClaim?.policy?.utilization_required_documents || []) {
      const v = String(slug || '').trim();
      if (!v || seen.has(v)) continue;
      seen.add(v);
      const match = DEFAULT_UC_DOC_TYPES.find((d) => d.value === v);
      out.push(match || { value: v, label: prettifyDocType(v) });
    }
    for (const d of DEFAULT_UC_DOC_TYPES) {
      if (!seen.has(d.value)) {
        seen.add(d.value);
        out.push(d);
      }
    }
    return out;
  }, [activeClaim?.policy?.utilization_required_documents]);

  useEffect(() => {
    if (ucDocTypeOptions.length === 0) return;
    if (!ucDocTypeOptions.some((o) => o.value === ucDocType)) {
      setUcDocType(ucDocTypeOptions[0].value);
    }
  }, [ucDocTypeOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        axios.get(`${API}/portal/claims`, { withCredentials: true }),
        axios.get(`${API}/portal/policies`, { withCredentials: true }),
      ]);
      setClaims(c.data || []);
      setPolicies(p.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('common.loading'));
    } finally {
      setLoading(false);
    }
  };

  const fetchClaim = async (id) => {
    // Clear stale state immediately so we never flash the previous claim's
    // AI review while the new one is in flight.
    setActiveClaim(null);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/portal/claims/${id}`, { withCredentials: true });
      // Iter51 — Wizard is now the ONLY supported flow. ALL claims
      // (regardless of policy.wizard_enabled flag) redirect to the
      // wizard URL. Legacy free-form UI is retained as dead code for
      // a future archive-view feature, but is unreachable.
      navigate(`/portal/claim-wizard/${id}`);
      return;
    } catch (e) {
      toast.error(e.response?.data?.detail || t('common.loading'));
      setActiveClaim(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Deep-link: ?policy=xxx auto-opens the New Claim dialog
  useEffect(() => {
    if (policyIdFromUrl && policies.length > 0) {
      const p = policies.find((x) => x.id === policyIdFromUrl);
      if (p) {
        setCreateForm({
          policy_id: p.id,
          claim_amount: p.eligible_amount || '',
          notes: '',
        });
        setCreateOpen(true);
      }
      params.delete('policy');
      setParams(params, { replace: true });
    }
  }, [policyIdFromUrl, policies]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link: ?id=xxx opens detail view
  useEffect(() => {
    if (claimIdFromUrl) fetchClaim(claimIdFromUrl);
  }, [claimIdFromUrl]);

  // Review-on-open: when a claim detail opens, run an incremental AI review so
  // any admin prompt change since the last review is applied to the existing
  // documents (the backend re-judges verdicts stamped under an older prompt).
  // Cheap no-op when nothing is stale. Non-blocking and non-fatal.
  const onOpenReviewRef = useRef(null);
  useEffect(() => {
    const id = activeClaim?.id;
    if (!id) { onOpenReviewRef.current = null; return; }
    if (!(activeClaim?.attachments?.length)) return;
    if (onOpenReviewRef.current === id) return;
    onOpenReviewRef.current = id;
    (async () => {
      try {
        await axios.post(`${API}/portal/claims/${id}/ai-review?only_new=true`, {}, { withCredentials: true });
        await pollClaimUntilReviewDone(id);
      } catch { /* keep cached verdicts on failure */ }
    })();
  }, [activeClaim?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Handlers ──────────────────────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await axios.post(`${API}/portal/claims`, {
        policy_id: createForm.policy_id,
        claim_amount: Number(createForm.claim_amount) || 0,
        notes: createForm.notes,
      }, { withCredentials: true });
      toast.success(t('claims.dialog.create.submit'));
      setCreateOpen(false);
      setCreateForm({ policy_id: '', claim_amount: '', notes: '' });
      // Iter51 — wizard is the only supported flow now; always redirect.
      navigate(`/portal/claim-wizard/${res.data.claim_id}`);
      return;
    } catch (err) {
      toast.error(err.response?.data?.detail || t('claims.dialog.create.submit'));
    } finally {
      setCreating(false);
    }
  };

  // Old signature: `handleFileUpload(event)` reading file + the dropdown's
  // `uploadingDocType`. New signature: `(file, slug)` so the per-row
  // uploader can lock the doc_type to its own slug. Keeps everything else
  // (auto-rerun AI, toasts) identical.
  const [uploadingSlug, setUploadingSlug] = useState(null);
  // Coalesce auto-AI-review across rapid multi-doc uploads. Without this,
  // 5 quick uploads fire 5 concurrent ai-review calls — Claude rate-limits
  // and the merged per_doc on the claim flips back and forth. We instead
  // wait until the secretary STOPS uploading for AI_DEBOUNCE_MS, then run
  // ONE incremental review covering every new attachment at once.
  const aiDebounceRef = useRef(null);
  const AI_DEBOUNCE_MS = 1800;
  const scheduleAiReview = () => {
    if (aiDebounceRef.current) clearTimeout(aiDebounceRef.current);
    aiDebounceRef.current = setTimeout(() => {
      aiDebounceRef.current = null;
      runAutoAiReview();
    }, AI_DEBOUNCE_MS);
  };

  // The AI review runs in the background in small batches. Poll the claim so
  // per-doc verdicts and the progress bar update live; stop on done/error or
  // after ~2 min. Each poll refreshes activeClaim (guarded by id).
  const pollClaimUntilReviewDone = async (id) => {
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      let data;
      try {
        const res = await axios.get(`${API}/portal/claims/${id}`, { withCredentials: true });
        data = res.data;
      } catch { continue; }
      setActiveClaim((prev) => (prev && prev.id === id ? data : prev));
      const st = data?.ai_review?.progress?.state;
      if (st === 'done' || st === 'error') return data;
    }
    return null;
  };

  const handleFileUpload = async (file, slug) => {
    if (!file || !activeClaim) return;
    setUploading(true);
    setUploadingSlug(slug);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(
        `${API}/portal/claims/${activeClaim.id}/attachments?doc_type=${slug}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      await fetchClaim(activeClaim.id);
      await fetchAll();
      // Auto AI review on upload has been removed entirely. Admins still
      // get the verdict via the manual "Re-run AI" button on their side,
      // and secretaries can re-run it themselves if they want a sanity check.
      // The previous auto-trigger could leave aiRunning=true indefinitely
      // if polling hung — blocking other parts of the UI for no benefit.
    } catch (err) {
      toast.error(err.response?.data?.detail || t('kyc.uploadFailed'));
    } finally {
      setUploading(false);
      setUploadingSlug(null);
    }
  };

  const runAutoAiReview = async () => {
    if (!activeClaim) return;
    const id = activeClaim.id;
    setAiRunning(true);
    try {
      // Incremental review — only the NEWLY uploaded files are graded. The
      // backend processes them in small batches in the background; we poll for
      // per-doc verdicts + progress so a bulk multi-doc upload never times out.
      await axios.post(
        `${API}/portal/claims/${id}/ai-review?only_new=true`, {},
        { withCredentials: true },
      );
      await pollClaimUntilReviewDone(id);
    } catch {
      // silent — user can still trigger manually via the "Re-run AI" button
    } finally {
      setAiRunning(false);
    }
  };

  const handleDeleteAttachment = async (attId) => {
    if (!activeClaim) return;
    if (!window.confirm(t('common.confirmRemove', { filename: '' }))) return;
    try {
      await axios.delete(
        `${API}/portal/claims/${activeClaim.id}/attachments/${attId}`,
        { withCredentials: true },
      );
      toast.success(t('kyc.removed'));
      await fetchClaim(activeClaim.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('kyc.deleteFailed'));
    }
  };

  // ── Inline edit: claim_amount (draft / sent-back only) ──────────────
  const startEditAmount = () => {
    if (!activeClaim) return;
    setAmountDraft(String(activeClaim.claim_amount ?? ''));
    setEditingAmount(true);
  };
  const cancelEditAmount = () => {
    setEditingAmount(false);
    setAmountDraft('');
  };
  const saveAmount = async () => {
    if (!activeClaim) return;
    const next = Number(amountDraft);
    if (!Number.isFinite(next) || next <= 0) {
      toast.error(t('claims.dialog.create.amount'));
      return;
    }
    const cap = Number(activeClaim.policy?.eligible_amount || 0);
    if (cap > 0 && next > cap) {
      toast.error(
        t('claims.detail.amountTooHigh', { amount: cap.toLocaleString(lang === 'hi' ? 'hi-IN' : 'en-IN') }),
      );
      return;
    }
    setSavingAmount(true);
    try {
      await axios.put(
        `${API}/portal/claims/${activeClaim.id}`,
        { claim_amount: next },
        { withCredentials: true },
      );
      toast.success(t('claims.detail.amountSaved'));
      setEditingAmount(false);
      setAmountDraft('');
      await fetchClaim(activeClaim.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('claims.detail.amountTooHigh', { amount: cap }));
    } finally {
      setSavingAmount(false);
    }
  };

  // ── Delete a draft claim (with confirm dialog) ──────────────────────
  const handleDeleteDraft = async () => {
    if (!confirmDelete) return;
    setDeletingClaim(true);
    try {
      await axios.delete(`${API}/portal/claims/${confirmDelete.id}`, { withCredentials: true });
      toast.success(t('claims.delete.success'));
      setConfirmDelete(null);
      // If the deleted claim was open in detail view, fall back to the list.
      if (activeClaim?.id === confirmDelete.id) {
        setParams({});
        setActiveClaim(null);
      }
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete claim');
    } finally {
      setDeletingClaim(false);
    }
  };

  const handleSubmitClaim = async () => {
    try {
      if (!activeClaim) {
        toast.error('Claim is not loaded yet — please refresh and try again');
        return;
      }
      // Client-side guard: catch missing required docs BEFORE we even hit
      // the server, so the secretary sees the actionable list immediately
      // instead of a generic 400. Server still re-validates as the source of truth.
      const required = activeClaim.policy?.required_documents || [];
      const uploadedTypes = new Set((activeClaim.attachments || []).map((a) => a.doc_type));
      const missing = required.filter((slug) => !uploadedTypes.has(slug));
      if (missing.length > 0) {
        toast.error(
          `Missing required document(s): ${missing.map((m) => m.replace(/_/g, ' ')).join(', ')}`,
          { duration: 8000 },
        );
        return;
      }
      // Submit is now decoupled from AI review entirely — the division can
      // submit at any time regardless of the AI verdict (or whether one
      // exists at all). The existing verdict (if any) is still shown inside
      // the confirmation modal for the secretary's awareness; we never
      // re-trigger or wait for the review.
      setPreSubmitOpen(true);
    } catch (err) {
      // Any unexpected error from the handler must NEVER silently swallow
      // — otherwise users see the dreaded "button does nothing" symptom.
      // Surface it as a toast so we at least get a screenshot to debug.
      // eslint-disable-next-line no-console
      console.error('handleSubmitClaim failed:', err);
      toast.error(`Submit failed: ${err?.message || 'unknown error'} — please refresh and retry`);
    }
  };

  // Actually fires the submit/resubmit endpoint after the secretary
  // confirms inside the pre-submit modal.
  const handleConfirmSubmit = async () => {
    if (!activeClaim) return;
    const isResubmit = activeClaim.sent_back_pending;
    setSubmittingConfirmed(true);
    const tid = toast.loading(t('claims.preSubmit.submitting'));
    try {
      await axios.post(
        `${API}/portal/claims/${activeClaim.id}/submit`, {},
        { withCredentials: true, timeout: 120000 },
      );
      toast.success(
        isResubmit ? t('claims.preSubmit.resubmitted') : t('claims.preSubmit.submitted'),
        { id: tid },
      );
      setPreSubmitOpen(false);
      await fetchClaim(activeClaim.id);
      await fetchAll();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && Array.isArray(detail.missing)) {
        toast.error(
          `${detail.message} (${detail.missing.map((m) => m.replace(/_/g, ' ')).join(', ')})`,
          { id: tid, duration: 8000 },
        );
      } else {
        toast.error(detail || t('claims.preSubmit.submitFailed'), { id: tid });
      }
    } finally {
      setSubmittingConfirmed(false);
    }
  };

  const handleRerunAi = async () => {
    if (!activeClaim) return;
    setAiRunning(true);
    const t = toast.loading('Re-running AI review…');
    try {
      const id = activeClaim.id;
      await axios.post(
        `${API}/portal/claims/${id}/ai-review`, {},
        { withCredentials: true },
      );
      const finalClaim = await pollClaimUntilReviewDone(id);
      const score = finalClaim?.ai_review?.satisfaction_score;
      if (score != null) {
        toast.success(`AI verdict ready — score ${score}`, { id: t });
      } else {
        toast.info('AI review complete', { id: t });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI review failed', { id: t });
    } finally {
      setAiRunning(false);
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const res = await axios.get(
        `${API}/portal/claims/${activeClaim.id}/attachments/${att.id}/download`,
        { responseType: 'blob', withCredentials: true },
      );
      const blob = new Blob([res.data], { type: att.content_type || res.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = att.filename;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(t('kyc.downloadFailed'));
    }
  };

  // ─── Utilisation Certificate handlers (post-payout) ───────────────────
  const handleUcUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeClaim) return;
    setUcUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(
        `${API}/portal/claims/${activeClaim.id}/utilization-documents?doc_type=${ucDocType}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success('Utilisation document uploaded');
      await fetchClaim(activeClaim.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('kyc.uploadFailed'));
    } finally {
      setUcUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteUcDoc = async (docId) => {
    if (!activeClaim) return;
    if (!window.confirm('Remove this utilisation document?')) return;
    try {
      await axios.delete(
        `${API}/portal/claims/${activeClaim.id}/utilization-documents/${docId}`,
        { withCredentials: true },
      );
      toast.success(t('kyc.removed'));
      await fetchClaim(activeClaim.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('kyc.deleteFailed'));
    }
  };

  const downloadUcDoc = async (doc) => {
    try {
      const res = await axios.get(
        `${API}/portal/claims/${activeClaim.id}/utilization-documents/${doc.id}/download`,
        { responseType: 'blob', withCredentials: true },
      );
      const blob = new Blob([res.data], { type: doc.content_type || res.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = doc.filename;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('kyc.downloadFailed'));
    }
  };

  // ─── Detail view ───────────────────────────────────────────────────────
  if (claimIdFromUrl) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Button
            variant="ghost"
            onClick={() => { setActiveClaim(null); setParams({}, { replace: true }); }}
            data-testid="claim-back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('claims.backToList')}
          </Button>
          {/* Submit lives up here so it's reachable on every viewport without
              fighting the "Made with Emergent" badge at the bottom right. */}
          {activeClaim?.is_editable && (() => {
            const required = activeClaim.policy?.required_documents || [];
            const uploadedTypes = new Set((activeClaim.attachments || []).map((a) => a.doc_type));
            const missingCount = required.filter((s) => !uploadedTypes.has(s)).length;
            const noAttachments = (activeClaim.attachments || []).length === 0;
            // AI state is NEVER allowed to block submit. The only valid
            // reasons to block are missing required documents or no
            // attachments at all — both real business rules.
            const blocked = noAttachments || missingCount > 0;
            return (
              <Button
                onClick={handleSubmitClaim}
                disabled={blocked}
                data-testid="submit-claim-button"
                title={
                  noAttachments
                    ? t('claims.detail.section.docsHint')
                    : missingCount > 0
                    ? t('claims.detail.submitBlocked', { n: missingCount })
                    : ''
                }
              >
                <Send className="h-4 w-4 mr-2" />
                {activeClaim.sent_back_pending ? t('claims.detail.resubmit') : t('claims.detail.submit')}
              </Button>
            );
          })()}
        </div>

        {detailLoading || !activeClaim ? (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
          </div>
        ) : (
          <>
            <ClaimTimeline claim={activeClaim} />
            <Card>
              <CardHeader className="border-b border-border/50">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="font-heading">
                      {activeClaim.policy?.title || t('claims.title')}
                    </CardTitle>
                    <div className="text-sm text-muted-foreground mt-1">
                      {activeClaim.policy?.fiscal_year} ·
                      {' '}{t('dash.eligible')} ₹{(activeClaim.policy?.eligible_amount || 0).toLocaleString('en-IN')}
                    </div>
                  </div>
                  <Badge className={`${STATUS_TONE[activeClaim.status] || 'bg-muted'} text-sm px-3 py-1`}>
                    {tStatus(activeClaim.status)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-5 space-y-4">
                {activeClaim.approved_amount != null && activeClaim.approved_amount !== activeClaim.claim_amount && (
                  <div
                    className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 flex items-center justify-between gap-3 flex-wrap"
                    data-testid="approved-amount-banner"
                  >
                    <div className="text-xs text-emerald-900">
                      <strong>{t('claims.detail.approvedBanner', { claimed: (activeClaim.claim_amount || 0).toLocaleString('en-IN'), approved: (activeClaim.approved_amount || 0).toLocaleString('en-IN') })}</strong>
                      {activeClaim.approval_note && (
                        <span className="block mt-0.5 italic text-emerald-900/80">&ldquo;{activeClaim.approval_note}&rdquo;</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">You claimed</div>
                        <div className="font-bold tabular-nums line-through text-muted-foreground">
                          ₹{(activeClaim.claim_amount || 0).toLocaleString('en-IN')}
                        </div>
                      </div>
                      <div className="text-emerald-700 text-lg">→</div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-emerald-700">Approved</div>
                        <div className="font-bold tabular-nums text-emerald-800">
                          ₹{(activeClaim.approved_amount || 0).toLocaleString('en-IN')}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">
                      {activeClaim.approved_amount != null ? t('claims.detail.approvedAmount') : t('claims.detail.claimAmount')}
                    </div>
                    {/* Inline-edit affordance — only when the claim is still
                        editable (draft / sent back). Once submitted, the amount
                        is locked. The pencil button reveals an inline number
                        input + Save/Cancel pair. */}
                    {(() => {
                      const editable = (
                        activeClaim.status === 'draft' || activeClaim.sent_back_pending
                      ) && activeClaim.approved_amount == null;
                      if (editingAmount && editable) {
                        const cap = Number(activeClaim.policy?.eligible_amount || 0);
                        return (
                          <div className="mt-1 space-y-1">
                            <div className="flex items-center gap-1.5">
                              <Input
                                type="number" min="0" step="1"
                                max={cap || undefined}
                                value={amountDraft}
                                onChange={(e) => setAmountDraft(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') { e.preventDefault(); saveAmount(); }
                                  if (e.key === 'Escape') { e.preventDefault(); cancelEditAmount(); }
                                }}
                                autoFocus
                                className="h-8 text-sm font-semibold tabular-nums w-32"
                                data-testid="claim-amount-edit-input"
                              />
                              <Button
                                type="button" size="sm"
                                onClick={saveAmount}
                                disabled={savingAmount}
                                className="h-8"
                                data-testid="claim-amount-save"
                              >
                                {savingAmount
                                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  : <Check className="h-3.5 w-3.5" />}
                              </Button>
                              <Button
                                type="button" size="sm" variant="ghost"
                                onClick={cancelEditAmount}
                                disabled={savingAmount}
                                className="h-8"
                                data-testid="claim-amount-cancel"
                              >
                                <IconX className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                            {cap > 0 && (
                              <p className="text-[11px] text-muted-foreground">
                                {t('claims.dialog.create.maxEligible', { amount: cap.toLocaleString(lang === 'hi' ? 'hi-IN' : 'en-IN') })}
                              </p>
                            )}
                          </div>
                        );
                      }
                      return (
                        <div className="font-semibold tabular-nums mt-0.5 flex items-center gap-2">
                          <span>
                            ₹{((activeClaim.approved_amount ?? activeClaim.claim_amount) || 0).toLocaleString(lang === 'hi' ? 'hi-IN' : 'en-IN')}
                          </span>
                          {editable && (
                            <button
                              type="button"
                              onClick={startEditAmount}
                              className="text-emerald-700 hover:text-emerald-900 transition"
                              title={t('claims.detail.editAmount')}
                              aria-label={t('claims.detail.editAmount')}
                              data-testid="claim-amount-edit-btn"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">{lang === 'hi' ? 'बनाया' : 'Created'}</div>
                    <div className="text-sm mt-0.5">
                      {activeClaim.created_at ? new Date(activeClaim.created_at).toLocaleDateString(lang === 'hi' ? 'hi-IN' : 'en-IN') : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('claims.table.submittedAt')}</div>
                    <div className="text-sm mt-0.5">
                      {activeClaim.submitted_at ? new Date(activeClaim.submitted_at).toLocaleDateString(lang === 'hi' ? 'hi-IN' : 'en-IN') : '—'}
                    </div>
                  </div>
                </div>
                {activeClaim.notes && (
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{t('claims.dialog.create.notes')}</div>
                    <div className="text-sm whitespace-pre-wrap">{activeClaim.notes}</div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Required Documents — one row per slug with inline upload +
                AI Observation + AI Recommendation. Replaces the old global
                dropdown + table UX. */}
            <Card>
              <CardHeader className="border-b border-border/50">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <CardTitle className="text-base font-heading flex items-center gap-2">
                    <FileText className="h-4 w-4 text-emerald-700" />
                    {t('claims.detail.section.docs')}
                    <span className="text-xs text-muted-foreground font-normal ml-1">
                      ({activeClaim.attachments?.length || 0} {t('common.uploaded').toLowerCase()})
                    </span>
                  </CardTitle>
                  {/* Submit-readiness banner: green when all required slugs
                      have at least one file, amber otherwise. */}
                  {activeClaim.is_editable && (activeClaim.policy?.required_documents || []).length > 0 && (() => {
                    const uploadedTypes = new Set((activeClaim.attachments || []).map((a) => a.doc_type));
                    const required = activeClaim.policy.required_documents;
                    const missing = required.filter((s) => !uploadedTypes.has(s));
                    if (missing.length === 0) {
                      return (
                        <Badge className="bg-emerald-600 hover:bg-emerald-600 text-[11px] gap-1" data-testid="checklist-complete">
                          <Check className="h-3 w-3" /> {lang === 'hi' ? 'सभी अनिवार्य अपलोड हो गए' : 'All required uploaded'}
                        </Badge>
                      );
                    }
                    return (
                      <Badge variant="outline" className="text-[11px] gap-1 text-amber-700 border-amber-300" data-testid="checklist-missing">
                        <RotateCcw className="h-3 w-3" /> {missing.length} {t('docs.pendingBadge').toLowerCase()}
                      </Badge>
                    );
                  })()}
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <RequiredDocsUploader
                  claim={activeClaim}
                  editable={activeClaim.is_editable}
                  uploadingSlug={uploadingSlug}
                  aiRunning={aiRunning}
                  onUpload={handleFileUpload}
                  onDelete={activeClaim.is_editable ? handleDeleteAttachment : null}
                  onPreview={(att) => setPreviewAttachment(att)}
                />
              </CardContent>
            </Card>

            {/* AI review — available on draft + every later stage so the
                secretary can preview verdict + missing docs BEFORE
                submitting and fix issues. */}
            {(activeClaim.attachments || []).length > 0 && (
              <Card data-testid="ai-review-card">
                <CardHeader className="border-b border-border/50">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <CardTitle className="text-base font-heading flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-amber-600" />
                      {t('claims.detail.section.aiReview')}
                      {activeClaim.ai_review?.satisfaction_score != null && (
                        <Badge className={
                          activeClaim.ai_review.satisfaction_score >= 80
                            ? 'bg-emerald-600 hover:bg-emerald-600'
                            : activeClaim.ai_review.satisfaction_score >= 50
                            ? 'bg-amber-500 hover:bg-amber-500'
                            : 'bg-rose-600 hover:bg-rose-600'
                        }>{activeClaim.ai_review.satisfaction_score}/100</Badge>
                      )}
                    </CardTitle>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleRerunAi}
                      disabled={aiRunning}
                      data-testid="rerun-ai-button"
                    >
                      {aiRunning
                        ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />{t('claims.detail.aiRunning')}</>
                        : <><Sparkles className="h-3.5 w-3.5 mr-1.5" />{activeClaim.ai_review ? t('claims.detail.rerunAi') : t('claims.detail.runAi')}</>
                      }
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 space-y-2 text-sm">
                  {activeClaim.ai_review?.progress?.state === 'processing' && (() => {
                    const pr = activeClaim.ai_review.progress;
                    const pct = pr.total ? Math.round((pr.done / pr.total) * 100) : 0;
                    return (
                      <div className="mb-1" data-testid="ai-progress">
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
                          <span className="flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            {lang === 'hi' ? 'दस्तावेज़ जाँचे जा रहे हैं…' : 'Checking documents…'}
                          </span>
                          <span className="tabular-nums">{pr.done}/{pr.total}</span>
                        </div>
                        <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
                          <div className="h-full bg-emerald-600 transition-all duration-500" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })()}
                  {/* AI failure banner — surfaces whenever the most
                      recent AI call failed (budget exhausted, rate
                      limit, network). We keep the prior ai_review
                      visible underneath so the secretary still has
                      SOMETHING actionable. */}
                  {activeClaim.ai_error && (
                    <div
                      className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 flex items-start gap-2"
                      data-testid="ai-error-banner"
                    >
                      <AlertCircle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
                      <div className="text-xs text-rose-900 leading-snug">
                        <div className="font-semibold mb-0.5">
                          {lang === 'hi' ? 'AI समीक्षा अनुपलब्ध' : 'AI review unavailable'}
                        </div>
                        <div>{activeClaim.ai_error.message}</div>
                        {activeClaim.ai_review && (
                          <div className="text-[10px] text-rose-700/80 mt-1">
                            {lang === 'hi'
                              ? 'पिछले निर्णय नीचे दिखाए गए हैं।'
                              : 'Showing the previous verdict below.'}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Language mismatch banner — surfaces when the cached
                      AI review was generated in a different language than
                      the secretary is currently reading the portal in.
                      Single-click "Re-run in <lang>" regenerates the
                      prose in their language. */}
                  {activeClaim.ai_review && activeClaim.ai_review.lang
                    && activeClaim.ai_review.lang !== lang && (
                    <div
                      className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2 flex items-center justify-between gap-3 flex-wrap"
                      data-testid="ai-lang-mismatch-banner"
                    >
                      <div className="text-xs text-sky-900 leading-snug">
                        {lang === 'hi'
                          ? 'यह AI निर्णय अंग्रेज़ी में बनाया गया था। हिन्दी में देखने के लिए AI पुनः चलाएँ।'
                          : 'This AI verdict was generated in Hindi. Re-run AI to see it in English.'}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleRerunAi}
                        disabled={aiRunning}
                        className="border-sky-400 text-sky-900 hover:bg-sky-100"
                        data-testid="rerun-ai-lang"
                      >
                        {aiRunning
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <><Sparkles className="h-3.5 w-3.5 mr-1.5" />{lang === 'hi' ? 'हिन्दी में AI चलाएँ' : 'Re-run in English'}</>}
                      </Button>
                    </div>
                  )}
                  {!activeClaim.ai_review ? (
                    <p className="text-xs text-muted-foreground italic">
                      {lang === 'hi' ? 'अभी कोई AI निर्णय नहीं। ' : 'No AI verdict yet. Click '}<strong>{t('claims.detail.runAi')}</strong>{lang === 'hi' ? ' पर क्लिक करें — आमतौर पर ~15 सेकंड लगते हैं।' : ' — it usually takes ~15s.'}
                    </p>
                  ) : (
                    <>
                      {activeClaim.ai_review.comments && (
                        <p className="whitespace-pre-wrap">{activeClaim.ai_review.comments}</p>
                      )}
                      {(activeClaim.ai_review.missing_documents || []).length > 0 && (
                        <div>
                          <div className="text-xs uppercase tracking-wider text-muted-foreground mt-2">{t('docs.missingBadge')}</div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {activeClaim.ai_review.missing_documents.map((m) => (
                              <Badge key={m} variant="destructive" className="text-xs break-all max-w-full">
                                {tDocType(m)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Utilisation Certificate (post-payout) */}
            {activeClaim.status === 'paid' && (
              <Card data-testid="utilization-card">
                <CardHeader className="border-b border-border/50">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <CardTitle className="text-base font-heading flex items-center gap-2">
                        <FileText className="h-4 w-4 text-emerald-700" />
                        {t('doctype.utilization_certificate')}
                        <span className="text-xs text-muted-foreground font-normal ml-1">
                          ({(activeClaim.utilization_documents || []).length})
                        </span>
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">
                        {lang === 'hi'
                          ? 'अनुदान भुगतान हो चुका है — कृपया उपयोगिता प्रमाणपत्र, पूर्णता फ़ोटोग्राफ़ और एक संक्षिप्त पूर्णता रिपोर्ट अपलोड करें।'
                          : 'Now that the grant is paid, please upload your Utilisation Certificate, completion photographs and a brief completion report.'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={ucDocType} onValueChange={setUcDocType}>
                        <SelectTrigger className="w-[200px]" data-testid="uc-doc-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ucDocTypeOptions.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{tDocType(o.value)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <label>
                        <Button asChild disabled={ucUploading} data-testid="uc-upload-button">
                          <span className="cursor-pointer">
                            <Upload className={`h-4 w-4 mr-2 ${ucUploading ? 'animate-pulse' : ''}`} />
                            {ucUploading ? t('common.uploading') : t('common.upload')}
                          </span>
                        </Button>
                        <input
                          type="file"
                          className="hidden"
                          accept="application/pdf"
                          onChange={handleUcUpload}
                        />
                      </label>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {(activeClaim.utilization_documents || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground py-6 text-center">
                      {t('docs.noDocsYet')}
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{lang === 'hi' ? 'प्रकार' : 'Type'}</TableHead>
                          <TableHead>{t('common.filename')}</TableHead>
                          <TableHead>{t('common.size')}</TableHead>
                          <TableHead>{t('common.uploaded')}</TableHead>
                          <TableHead className="text-right">{t('common.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(activeClaim.utilization_documents || []).map((d) => (
                          <TableRow key={d.id} data-testid={`uc-row-${d.id}`}>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {tDocType(d.doc_type) || t('doctype.other')}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs">{d.filename}</TableCell>
                            <TableCell className="tabular-nums text-xs">
                              {d.size_bytes ? `${Math.round(d.size_bytes / 1024)} KB` : '—'}
                            </TableCell>
                            <TableCell className="text-xs">
                              {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString(lang === 'hi' ? 'hi-IN' : 'en-IN') : '—'}
                            </TableCell>
                            <TableCell className="text-right space-x-1">
                              <Button size="sm" variant="ghost" onClick={() => downloadUcDoc(d)}>
                                <Download className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handleDeleteUcDoc(d.id)}>
                                <Trash2 className="h-4 w-4 text-rose-600" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Status history */}
            {(activeClaim.status_history || []).length > 0 && (
              <Card>
                <CardHeader className="border-b border-border/50">
                  <CardTitle className="text-base font-heading">{lang === 'hi' ? 'गतिविधि' : 'Activity'}</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <ol className="space-y-3">
                    {activeClaim.status_history.map((h, i) => (
                      <li key={`${h.status}-${h.at || i}`} className="flex items-start gap-3 text-sm">
                        <div className="h-2 w-2 rounded-full bg-emerald-600 mt-1.5 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="font-medium">
                            {tStatus(h.status)}
                            {h.note && <span className="text-muted-foreground font-normal"> · {h.note}</span>}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {h.at ? new Date(h.at).toLocaleString(lang === 'hi' ? 'hi-IN' : 'en-IN') : ''} · {h.by || ''}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            )}

            {activeClaim.is_editable && (activeClaim.attachments || []).length === 0 && (
              <div className="text-xs text-muted-foreground text-center pt-2">
                {t('claims.detail.section.docsHint')}
              </div>
            )}
          </>
        )}

        {/* Preview modal — rendered INSIDE the claim-detail branch so the
            eye icon on every uploaded attachment can open it. Was previously
            only mounted in the list-view return below, which is why clicking
            the eye did nothing once the side-sheet was open. */}
        <DocumentPreviewModal
          open={!!previewAttachment}
          onClose={() => setPreviewAttachment(null)}
          title={previewAttachment?.filename}
          url={previewUrl}
          contentType={previewAttachment?.content_type}
        />
      </div>
    );
  }

  // ─── List view ─────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold">{t('claims.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('dash.subtitle')}</p>
        </div>
        <Button
          onClick={() => {
            const firstActive = policies[0];
            if (!firstActive) {
              toast.error(t('dash.noPolicies'));
              return;
            }
            setCreateForm({ policy_id: firstActive.id, claim_amount: firstActive.eligible_amount || '', notes: '' });
            setCreateOpen(true);
          }}
          data-testid="new-claim-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('claims.newClaim')}
        </Button>
      </div>

      {/* Claims grid — card-based layout, modern + scannable.
          Each card is a clickable surface that opens the claim; the
          trash icon (only for drafts) escapes the click bubbling to
          surface the destructive confirm dialog instead. */}
      {loading ? (
        <Card>
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            {t('common.loading')}
          </CardContent>
        </Card>
      ) : claims.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center space-y-3">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <FileText className="h-6 w-6" />
            </div>
            <div className="text-base font-medium">{t('claims.empty')}</div>
            <Button
              onClick={() => {
                const firstActive = policies[0];
                if (!firstActive) {
                  toast.error(t('dash.noPolicies'));
                  return;
                }
                setCreateForm({ policy_id: firstActive.id, claim_amount: firstActive.eligible_amount || '', notes: '' });
                setCreateOpen(true);
              }}
              size="sm"
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              {t('claims.newClaim')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {claims.map((c) => {
            const policy = policies.find((p) => p.id === c.policy_id);
            const isDraft = c.status === 'draft';
            const docsCount = (c.attachments || []).length;
            const isPaid = c.status === 'paid';
            return (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                onClick={() => setParams({ id: c.id })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setParams({ id: c.id });
                  }
                }}
                data-testid={`claim-card-${c.id}`}
                className="group relative rounded-xl border bg-card hover:border-emerald-400/60 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer p-5 focus:outline-none focus:ring-2 focus:ring-emerald-400/60"
              >
                {/* Top row — policy + status pill */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">
                      {c.fiscal_year}
                    </div>
                    <h3 className="font-semibold text-[15px] leading-snug mt-0.5 line-clamp-2">
                      {policy?.title || c.policy_id.slice(0, 8)}
                    </h3>
                  </div>
                  <Badge className={`${STATUS_TONE[c.status] || 'bg-muted'} text-[11px] whitespace-nowrap shrink-0`}>
                    {tStatus(c.status)}
                  </Badge>
                </div>

                {/* Amount block — prominent emerald accent */}
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {isPaid && c.approved_amount != null
                      ? t('claims.detail.approvedAmount')
                      : t('claims.detail.claimAmount')}
                  </div>
                  <div className="text-2xl font-bold text-emerald-800 tabular-nums leading-tight">
                    ₹{((c.approved_amount ?? c.claim_amount) || 0).toLocaleString(lang === 'hi' ? 'hi-IN' : 'en-IN')}
                  </div>
                </div>

                {/* Meta row */}
                <div className="mt-4 pt-3 border-t flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" />
                    <span>
                      {docsCount} {docsCount === 1 ? t('claims.card.docLabel') : t('claims.card.docsLabel')}
                    </span>
                  </div>
                  <div className="tabular-nums">
                    {c.updated_at ? new Date(c.updated_at).toLocaleDateString(lang === 'hi' ? 'hi-IN' : 'en-IN') : '—'}
                  </div>
                </div>

                {/* Action row */}
                <div className="mt-4 flex items-center gap-2">
                  <Button
                    size="sm"
                    className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white"
                    onClick={(e) => { e.stopPropagation(); setParams({ id: c.id }); }}
                    data-testid={`claim-open-${c.id}`}
                  >
                    {t('claims.card.open')}
                    <ArrowLeft className="h-3.5 w-3.5 ml-1.5 rotate-180" />
                  </Button>
                  {isDraft && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 px-2"
                      onClick={(e) => { e.stopPropagation(); setConfirmDelete({ id: c.id, title: policy?.title || c.policy_id.slice(0, 8) }); }}
                      title={t('claims.card.delete')}
                      aria-label={t('claims.card.delete')}
                      data-testid={`claim-delete-${c.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}


      <DocumentPreviewModal
        open={!!previewAttachment}
        onClose={() => setPreviewAttachment(null)}
        title={previewAttachment?.filename}
        url={previewUrl}
        contentType={previewAttachment?.content_type}
      />

      {/* Delete-confirm for draft claims. Only renders when a target is set
          so the AlertDialog stays out of the accessibility tree otherwise. */}
      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('claims.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">{t('claims.delete.body')}</span>
              {confirmDelete && (
                <span className="block mt-2 font-medium text-foreground">
                  {confirmDelete.title}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deletingClaim}
              data-testid="claim-delete-cancel"
            >
              {t('claims.delete.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDraft}
              disabled={deletingClaim}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="claim-delete-confirm"
            >
              {deletingClaim
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : t('claims.delete.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Pre-submit AI review modal ──────────────────────────────────
          Opens on Submit click. While `preSubmitRunning` is true we show a
          spinner; once the AI verdict is back (or it failed), we render
          the score, missing-docs list, and per-doc verdict summary. The
          "Confirm submission" button is ALWAYS enabled — verdict is
          informational only. */}
      <Dialog
        open={preSubmitOpen}
        onOpenChange={(open) => {
          // Lock the modal closed while AI is still running OR while we're
          // mid-submit — otherwise a stray click outside the modal would
          // orphan the in-flight calls.
          if (!open && !preSubmitRunning && !submittingConfirmed) {
            setPreSubmitOpen(false);
          }
        }}
      >
        <DialogContent className="max-w-2xl w-[95vw] max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('claims.preSubmit.title')}</DialogTitle>
            <DialogDescription>{t('claims.preSubmit.subtitle')}</DialogDescription>
          </DialogHeader>
          {preSubmitRunning ? (
            <div className="py-10 flex flex-col items-center justify-center text-sm text-muted-foreground gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
              <div>{t('claims.preSubmit.running')}</div>
            </div>
          ) : (
            (() => {
              const ai = activeClaim?.ai_review || {};
              const score = Number.isFinite(ai.satisfaction_score) ? ai.satisfaction_score : null;
              const missing = ai.missing_documents || [];
              const perDoc = ai.per_doc || [];
              const ringTone = score == null
                ? 'text-muted-foreground border-muted'
                : score >= 80
                  ? 'text-emerald-700 border-emerald-400'
                  : score >= 50
                    ? 'text-amber-700 border-amber-400'
                    : 'text-rose-700 border-rose-400';
              return (
                <div className="space-y-5">
                  {/* Score + missing summary */}
                  <div className="flex items-center gap-5 flex-wrap">
                    <div className={`relative w-24 h-24 rounded-full border-4 ${ringTone} flex items-center justify-center bg-card`}>
                      <div className="text-3xl font-bold tabular-nums">
                        {score == null ? '—' : score}
                      </div>
                    </div>
                    <div className="flex-1 min-w-[200px] space-y-1">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground">
                        {t('claims.preSubmit.score')}
                      </div>
                      <div className="text-xs text-muted-foreground leading-relaxed">
                        {t('claims.preSubmit.scoreInfo')}
                      </div>
                    </div>
                  </div>

                  {/* Missing docs */}
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                      {t('claims.preSubmit.missing')}
                    </div>
                    {missing.length === 0 ? (
                      <div className="flex items-center gap-1.5 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                        <Check className="h-3.5 w-3.5" />
                        {t('claims.preSubmit.allCovered')}
                      </div>
                    ) : (
                      <ul className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 list-disc pl-7 space-y-0.5">
                        {missing.map((m) => (<li key={m}>{tDocType(m)}</li>))}
                      </ul>
                    )}
                  </div>

                  {/* Per-doc verdict — compact list */}
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                      {t('claims.preSubmit.perDoc')}
                    </div>
                    {perDoc.length === 0 ? (
                      <div className="text-xs text-muted-foreground italic">
                        {t('claims.preSubmit.none')}
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {perDoc.map((p, idx) => {
                          const v = p.verdict;
                          const tone = v === 'accepted'
                            ? 'border-l-emerald-500 bg-emerald-50/40'
                            : v === 'rejected'
                              ? 'border-l-rose-500 bg-rose-50/40'
                              : 'border-l-amber-500 bg-amber-50/40';
                          const Icon = v === 'accepted' ? CheckCircle2 : v === 'rejected' ? XCircle : AlertCircle;
                          const iconTone = v === 'accepted'
                            ? 'text-emerald-600'
                            : v === 'rejected'
                              ? 'text-rose-600'
                              : 'text-amber-600';
                          return (
                            <div
                              key={`${p.filename}-${idx}`}
                              className={`border-l-4 ${tone} rounded-r-md px-3 py-2`}
                            >
                              <div className="flex items-center gap-2">
                                <Icon className={`h-3.5 w-3.5 ${iconTone}`} />
                                <span className="font-mono text-[11px] truncate flex-1 min-w-0">{p.filename}</span>
                                <span className="text-[10px] text-muted-foreground">
                                  {tDocType(p.matched_slug || p.doc_type || 'other')}
                                </span>
                              </div>
                              {p.recommendation && v !== 'accepted' && (
                                <div className="text-[11px] text-foreground/70 mt-1 pl-5 leading-snug">
                                  {p.recommendation}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setPreSubmitOpen(false)}
              disabled={preSubmitRunning || submittingConfirmed}
              data-testid="presubmit-cancel"
            >
              {t('claims.preSubmit.cancel')}
            </Button>
            <Button
              onClick={handleConfirmSubmit}
              disabled={preSubmitRunning || submittingConfirmed}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="presubmit-confirm"
            >
              {submittingConfirmed
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><Send className="h-4 w-4 mr-1.5" /> {t('claims.preSubmit.confirm')}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('claims.dialog.create.title')}</DialogTitle>
            <DialogDescription className="sr-only">
              {t('claims.dialog.create.amountHint')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t('claims.dialog.create.policy')} *</Label>
              <Select
                value={createForm.policy_id}
                onValueChange={(v) => {
                  const p = policies.find((x) => x.id === v);
                  setCreateForm({ ...createForm, policy_id: v, claim_amount: p?.eligible_amount || '' });
                }}
              >
                <SelectTrigger data-testid="claim-policy-select" className="w-full">
                  <SelectValue
                    placeholder={t('claims.dialog.create.pickPolicy')}
                    className="truncate"
                  />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-3rem)]">
                  {policies.map((p) => (
                    <SelectItem key={p.id} value={p.id} className="whitespace-normal">
                      <span className="block">
                        <span className="font-medium">{p.title}</span>
                        <span className="text-muted-foreground"> · {p.fiscal_year}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(() => {
              const selected = policies.find((p) => p.id === createForm.policy_id);
              const cap = selected?.eligible_amount || 0;
              return (
                <div className="space-y-1.5">
                  <Label>{t('claims.dialog.create.amount')}</Label>
                  <Input
                    type="number" min="0" step="1" max={cap || undefined}
                    value={createForm.claim_amount}
                    onChange={(e) => setCreateForm({ ...createForm, claim_amount: e.target.value })}
                    placeholder={t('claims.dialog.create.amountHint')}
                    data-testid="claim-amount-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('claims.dialog.create.amountHint')}
                  </p>
                  {cap > 0 && (
                    <p className="text-xs text-emerald-800 font-medium">
                      {t('claims.dialog.create.maxEligible', { amount: cap.toLocaleString('en-IN') })}
                    </p>
                  )}
                </div>
              );
            })()}
            <div className="space-y-1.5">
              <Label>{t('claims.dialog.create.notes')}</Label>
              <Textarea
                rows={3}
                value={createForm.notes}
                onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                placeholder={t('claims.dialog.create.notesPlaceholder')}
                className="resize-y"
              />
            </div>
            <Button
              type="submit"
              disabled={creating || !createForm.policy_id}
              className="w-full"
              data-testid="claim-create-button"
            >
              {creating ? t('claims.dialog.create.submitting') : t('claims.dialog.create.submit')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalClaims;
